import { Component, inject, OnInit } from '@angular/core';
import { FileService } from 'app/core/file.service';
import { Attachment } from 'app/core/model/attachment';
import { EmailMessage } from 'app/core/model/email-message';
import { EmailMessageInvoiceLink, Invoice, Quote } from 'app/core/model/entity-model';
import { ReportService } from 'app/core/report.service';
import { alert, confirm } from 'devextreme/ui/dialog';
import validationEngine from 'devextreme/ui/validation_engine';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AppInformationService, AuthService, BrewersUnitOfWork, UtilityService } from '../../core/core.module';

export type EmailMode = 'Invoice' | 'Quote' | 'Customer';

@Component({
    standalone: false,
    selector: 'app-email-dialog',
    templateUrl: './email-dialog.component.html',
    styleUrls: ['./email-dialog.component.scss'],
})
export class EmailDialogComponent implements OnInit {
    modalRef = inject(BsModalRef);
    uow = inject(BrewersUnitOfWork);
    appInfo = inject(AppInformationService);
    fileService = inject(FileService);
    auth = inject(AuthService);
    reportService = inject(ReportService);
    utilityService = inject(UtilityService);

    result = new Subject<{ success: boolean; value?: EmailMessage }>();
    emailMessage: EmailMessage;
    emailMessageInvoiceLink: EmailMessageInvoiceLink;
    attachments: Attachment[] = [];
    mode: EmailMode;
    entity: Invoice | Quote;
    entityAttachments: Attachment[] = [];
    to: string;
    isNew: boolean;
    processing: boolean;
    multipleEmails: string = 'Sending to Mulitple Customers';

    uploadUrl: string;
    uploadHeader: any;

    async ngOnInit() {
        await this.appInfo.pleaseWaitShow();
        if (!this.emailMessage) {
            this.isNew = true;
            const entities = [];
            this.emailMessage = this.uow.emailMessages.createEntity({
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                to: this.to,
            });
            entities.push(this.emailMessage);
            if (this.mode === 'Invoice') {
                this.emailMessageInvoiceLink = this.uow.emailMessageInvoiceLinks.createEntity({
                    emailMessageId: this.emailMessage.id,
                    invoiceId: this.entity.id,
                });
                entities.push(this.emailMessageInvoiceLink);
                this.emailMessage.subject = (await this.uow.getSettingsByName('InvoiceSubject'))?.stringValue;
                this.emailMessage.body = `Please find Invoice ${(this.entity as Invoice).invoiceNumber} attached.`;
                const body = (await this.uow.getSettingsByName('InvoiceBody'))?.stringValue;
                if (body) this.emailMessage.body += `\n\n${body}`;
            } else if (this.mode === 'Quote') {
                this.emailMessage.quoteId = this.entity.id;
                this.emailMessage.subject = (await this.uow.getSettingsByName('QuoteSubject'))?.stringValue;
                this.emailMessage.body = (await this.uow.getSettingsByName('QuoteBody'))?.stringValue;
            } else if (this.mode === 'Customer') {

                // this.emailMessage.quoteId = this.entity.id;
                // this.emailMessage.subject = (await this.uow.getSettingsByName('QuoteSubject'))?.stringValue;
                // this.emailMessage.body = (await this.uow.getSettingsByName('QuoteBody'))?.stringValue;
            }
            this.copyEntityAttachments();
            entities.push(...this.attachments);
            await this.uow.commit(entities);
        } else {
            await this.getAttachments();
        }

        this.uploadUrl = this.fileService.getUploadUrl(this.emailMessage.id, FileService.Mode.EmailMessage);
        this.uploadHeader = this.fileService.getUploadHeader();
        await this.appInfo.pleaseWaitHide();
    }

    async getAttachments() {
        this.attachments = await this.uow.getAttachments(FileService.Mode.EmailMessage, this.emailMessage.id);
    }

    copyEntityAttachments() {
        if (!this.entityAttachments || this.entityAttachments.length === 0) return;

        this.entityAttachments.forEach((entityAttachment) => {
            this.attachments.push(
                this.uow.attachments.createEntity({
                    emailMessageId: this.emailMessage.id,
                    name: entityAttachment.name,
                    filename: entityAttachment.filename,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                })
            );
        });
    }

    async save() {
        if (this.processing) return;
        this.processing = true;

        const success = this.valid();
        if (!success) {
            this.processing = false;
            return;
        }

        this.appInfo.pleaseWaitShow();
        await this.uow.commit([this.emailMessage]);
        if (this.mode !== 'Customer') {
            const result = await this.utilityService.sendEmail(this.emailMessage.id);
            this.appInfo.pleaseWaitHide();

            if (result.success) await alert('Email Sent', this.appInfo.settings.company);
            else await alert(result.message, this.appInfo.settings.company);

            this.result.next({ success: true, value: this.emailMessage });
        } else {
            this.appInfo.pleaseWaitHide();
            this.result.next({ success: true, value: this.emailMessage });
        }
    }

    valid() {
        const groupConfig = validationEngine.getGroupConfig('emailDialogValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('emailDialogValidationGroup');
            return result.isValid;
        } else return false;
    }

    async cancel() {
        if (this.isNew) {
            const entities = [];
            this.emailMessage.entityAspect.setDeleted();
            entities.push(this.emailMessage);
            if (this.emailMessageInvoiceLink) {
                this.emailMessageInvoiceLink.entityAspect.setDeleted();
                entities.push(this.emailMessageInvoiceLink);
            }
            const attachments = await this.uow.getAttachments(FileService.Mode.EmailMessage, this.emailMessage.id, true);
            attachments.forEach((attachment) => attachment.entityAspect.setDeleted());
            entities.push(...attachments);
            await this.uow.commit(entities);
        } else {
            this.emailMessage.entityAspect.rejectChanges();
        }

        this.result.next({ success: false });
    }

    uploadStarted(e: any) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e: any) {
        var response = this.fileService.parseUploadResponse(e);
        if (!response.success) alert(`Unable to upload the file(s). ${response.message}`, 'Upload Error');
        else await this.getAttachments();
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e: any) {
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    openAttachment(attachment: Attachment) {
        if (!attachment) return;
        window.open(attachment.filename, '_blank');
    }

    async removeAttachment(attachment: Attachment) {
        if (!attachment) return;

        const success = await confirm('Are you sure you want to delete this attachment?', this.appInfo.settings.company);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        attachment.deleted = new Date();
        attachment.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit([attachment]);
        await this.getAttachments();
        this.appInfo.pleaseWaitHide();
    }

    openReport = async () => {
        if (this.mode === 'Invoice') await this.reportService.printInvoiceReport(this.entity as Invoice);
        else if (this.mode === 'Quote') await this.reportService.printQuoteReport(this.entity as Quote);
    };
}
